import React, { useState, useEffect } from "react";
import { showToast } from "./ToastNotification";
import useAuthToken from "../hooks/useAuthToken";
import { FaCheckCircle, FaTimesCircle, FaExclamationTriangle } from "react-icons/fa";
import { formatDate } from "../utils/dateUtils";
import Spinner from "./Spinner";
import analytics from "../services/analytics";

const SubscriptionManager = ({ onSubscriptionChange }) => {
  const { fetchWithAuth } = useAuthToken();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchSubscriptionDetails();
    // Track subscription management page view
    analytics.event('Subscription', 'view_management_page');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubscriptionDetails = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_API_BASE_URL}/api/stripe/subscription`,
        { method: "GET" }
      );

      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
        if (onSubscriptionChange) {
          onSubscriptionChange(data);
        }
      } else {
        showToast("Failed to fetch subscription details", "error");
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
      showToast("Error fetching subscription details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? It will remain active until the end of your billing period.")) {
      return;
    }

    // Track cancellation attempt
    const planName = subscription?.plan ? formatPlan(subscription.plan) : 'Unknown';
    
    setActionLoading(true);
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_API_BASE_URL}/api/stripe/subscription/cancel`,
        { method: "POST" }
      );

      if (response.ok) {
        // Track successful cancellation
        analytics.trackSubscriptionCancel(planName);
        analytics.trackButtonClick('subscription_cancelled', 'profile');
        
        showToast("Subscription scheduled for cancellation", "success");
        fetchSubscriptionDetails(); // Refresh data
      } else {
        const error = await response.json();
        showToast(error.detail || "Failed to cancel subscription", "error");
        
        // Track cancellation failure
        analytics.event('Subscription', 'cancel_failed', planName);
      }
    } catch (error) {
      console.error("Error canceling subscription:", error);
      showToast("Error canceling subscription", "error");
      
      // Track cancellation error
      analytics.event('Subscription', 'cancel_error', planName);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    // Track reactivation attempt
    const planName = subscription?.plan ? formatPlan(subscription.plan) : 'Unknown';
    
    setActionLoading(true);
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_API_BASE_URL}/api/stripe/subscription/reactivate`,
        { method: "POST" }
      );

      if (response.ok) {
        // Track successful reactivation
        analytics.event('Subscription', 'reactivated', planName);
        analytics.trackButtonClick('subscription_reactivated', 'profile');
        
        showToast("Subscription reactivated successfully", "success");
        fetchSubscriptionDetails(); // Refresh data
      } else {
        const error = await response.json();
        showToast(error.detail || "Failed to reactivate subscription", "error");
        
        // Track reactivation failure
        analytics.event('Subscription', 'reactivate_failed', planName);
      }
    } catch (error) {
      console.error("Error reactivating subscription:", error);
      showToast("Error reactivating subscription", "error");
      
      // Track reactivation error
      analytics.event('Subscription', 'reactivate_error', planName);
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    // Track billing portal access
    analytics.trackButtonClick('manage_billing', 'profile');
    
    setActionLoading(true);
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_API_BASE_URL}/api/stripe/subscription/billing-portal`,
        { method: "GET" }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Track successful billing portal redirect
        analytics.event('Subscription', 'billing_portal_accessed');
        
        window.location.href = data.url;
      } else {
        const error = await response.json();
        showToast(error.detail || "Failed to access billing portal", "error");
      }
    } catch (error) {
      console.error("Error accessing billing portal:", error);
      showToast("Error accessing billing portal", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-green-600";
      case "canceled":
        return "text-red-600";
      case "past_due":
        return "text-yellow-600";
      default:
        return "text-gray-600";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <FaCheckCircle className="text-green-500" />;
      case "canceled":
        return <FaTimesCircle className="text-red-500" />;
      case "past_due":
        return <FaExclamationTriangle className="text-yellow-500" />;
      default:
        return <FaTimesCircle className="text-gray-500" />;
    }
  };

  const formatPlan = (plan) => {
    if (!plan) return "No Plan";
    const [tier, period] = plan.split("_");
    return `${tier.charAt(0).toUpperCase() + tier.slice(1)} (${period === "month" ? "Monthly" : "Yearly"})`;
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center">
          <Spinner loading={true} />
        </div>
      </div>
    );
  }

  if (!subscription || !subscription.active) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          <FaTimesCircle className="mx-auto text-gray-400 text-4xl mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No Active Subscription</h3>
          <p className="text-gray-600 mb-4">You don't have an active subscription. Choose a plan to get started.</p>
          <a
            href="/products"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            View Plans
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-3 mb-6">
        {getStatusIcon(subscription.status)}
        <h2 className="text-2xl font-bold text-gray-800">Subscription Details</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Plan</h3>
          <p className="text-lg font-semibold text-gray-800">{formatPlan(subscription.plan)}</p>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Status</h3>
          <p className={`text-lg font-semibold ${getStatusColor(subscription.status)}`}>
            {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
          </p>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Credits Remaining</h3>
          <p className="text-lg font-semibold text-gray-800">{subscription.credits}</p>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Credits Reset</h3>
          <p className="text-lg font-semibold text-gray-800">
            {formatDate(subscription.limit_date)}
          </p>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Current Period Ends</h3>
          <p className="text-lg font-semibold text-gray-800">
            {formatDate(subscription.end_date)}
          </p>
        </div>

        {subscription.cancel_at_period_end && (
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Cancellation Status</h3>
            <p className="text-lg font-semibold text-red-600">Will cancel at period end</p>
          </div>
        )}
      </div>

      {subscription.cancel_at_period_end && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <FaExclamationTriangle className="text-yellow-600" />
            <p className="text-yellow-800">
              Your subscription is scheduled to cancel at the end of your billing period. You can reactivate it anytime before then.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={handleManageBilling}
          disabled={actionLoading}
          className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {actionLoading ? "Loading..." : "Manage Billing"}
        </button>

        {subscription.cancel_at_period_end ? (
          <button
            onClick={handleReactivateSubscription}
            disabled={actionLoading}
            className="flex-1 bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {actionLoading ? "Loading..." : "Reactivate Subscription"}
          </button>
        ) : (
          <button
            onClick={handleCancelSubscription}
            disabled={actionLoading}
            className="flex-1 bg-red-600 text-white px-6 py-3 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {actionLoading ? "Loading..." : "Cancel Subscription"}
          </button>
        )}
      </div>
    </div>
  );
};

export default SubscriptionManager;